import { useEffect, useState, memo, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import styled from "@emotion/styled";

import { meteringService } from "service/meteringService";
import localStorageService from "service/localStorageService";
import { isHttpSuccess, parseColorToHex } from "utils/functions";
import { ALL_MONTHS_LIST } from "constant";
import WidgetBox from "../WidgetBox";
import WidgetContent from "../WidgetContent";
import { WidgetChartTypeEnum } from "types/Dashboards";
import { WidgetProps, ChartPanel } from "types";

type MetricWidgetPanel = Omit<ChartPanel, "attributes"> & {
    attributes?: {
        color?: [number, string][];
        organization_id: string;
    }[];
};

type MetricWidgetProps = Omit<WidgetProps, "panel" | "attributes"> & {
    panel: MetricWidgetPanel;
};

const StyledWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const ControllerWrapper = styled.div`
    position: absolute;
    top: -16px;
    right: 0;
    z-index: 1;
`;

interface MonthlyDataState {
    emailData: number[];
    smsData: number[];
    notificationData: number[];
    dashboardData: number[];
    offloadData: number[];
    eventsEnabledData: number[];
    eventsTriggeredData: number[];
}

interface MonthlyDataBody {
    [key: string]: number[];
}

interface TokenLookup {
    [key: string]: string;
    emailData: string;
    smsData: string;
    notificationData: string;
    dashboardData: string;
    offloadData: string;
    eventsEnabledData: string;
    eventsTriggeredData: string;
}

const widgetTemplate = {
    [WidgetChartTypeEnum.NOTIFIER_COUNT]: {
        title: "Count of Email, SMS, Notification",
        subTitle: "Count of emails, SMS, and notifications sent",
        getChartSeries: (monthlyData: any) => [
            {
                name: "Email",
                type: "bar",
                stack: "one",
                barWidth: 25,
                data: monthlyData.emailData,
            },
            {
                name: "SMS",
                type: "bar",
                stack: "one",
                barWidth: 25,
                data: monthlyData.smsData,
            },
            {
                name: "Notification",
                type: "bar",
                stack: "one",
                barWidth: 25,
                data: monthlyData.notificationData,
            },
        ],
        showLabels: ["Email", "SMS", "Notification"],
    },
    [WidgetChartTypeEnum.COUNT_ALERT_TRIGGER]: {
        title: "Count of events",
        subTitle: "Count of Events enabled vs triggered",
        getChartSeries: (monthlyData: any) => [
            {
                name: "Events enabled",
                type: "bar",
                stack: "one",
                barWidth: 20,
                data: monthlyData.eventsEnabledData,
            },
            {
                name: "Events triggered",
                type: "bar",
                stack: "two",
                barWidth: 20,
                data: monthlyData.eventsTriggeredData,
            },
        ],
        showLabels: ["Events enabled", "Events triggered"],
    },
};

const YearDropdown = ({
    displayedYear,
    onChange,
    yearDropdown,
}: {
    displayedYear: number;
    onChange?: (year: number) => void;
    yearDropdown: number[];
}) => {
    const renderYear = () =>
        yearDropdown.map((year: number) => {
            return (
                <Dropdown.Item
                    key={year}
                    onClick={() => {
                        onChange?.(year);
                    }}
                >
                    {year}
                </Dropdown.Item>
            );
        });

    return (
        <div>
            <Dropdown alignRight>
                <Dropdown.Toggle
                    variant=""
                    style={{ color: "#08b39b", padding: 0 }}
                >
                    {displayedYear}
                </Dropdown.Toggle>
                <Dropdown.Menu key="years">{renderYear()}</Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

const EventCounterChart = (props: MetricWidgetProps) => {
    const orgId: string = localStorageService.getItem("orgId");
    const {
        panel: { name, source, attributes },
        isVisible,
        visibleRef,
    } = props;

    const widgetType =
        source === "EVENT"
            ? WidgetChartTypeEnum.COUNT_ALERT_TRIGGER
            : WidgetChartTypeEnum.NOTIFIER_COUNT;
    const displayConfig = widgetTemplate[widgetType];

    const { title, subTitle, getChartSeries, showLabels } = displayConfig;
    const [isLoading, setIsLoading] = useState(true);

    const [monthlyData, setMonthlyData] = useState<MonthlyDataState>({
        emailData: [],
        smsData: [],
        notificationData: [],
        dashboardData: [],
        offloadData: [],
        eventsEnabledData: [],
        eventsTriggeredData: [],
    });

    const [yearDropdown, setYearDropdown] = useState<number[]>([]);
    const [displayedYear, setDisplayedYear] = useState<number>(moment().year());

    useEffect(() => {
        const selected: {
            year: number;
        } = {
            year: displayedYear,
        };
        const getDataCount = async () => {
            const res: any = await meteringService.getMeteringInfo({
                year: String(selected.year),
                organization_id: orgId,
            });

            if (isHttpSuccess(res.status)) {
                const ownerOrg: string = Object.keys(res.data).find(
                    (k: string) => k !== "start_from"
                ) ?? "";

                const {
                    start_from: {
                        year: yearOfSubscription = moment().year(),
                    } = {},
                } = res.data;

                const monthlyDataBody: MonthlyDataBody = {
                    emailData: [],
                    smsData: [],
                    notificationData: [],
                    dashboardData: [],
                    offloadData: [],
                    eventsEnabledData: [],
                    eventsTriggeredData: [],
                };

                const tokenLookup: TokenLookup = {
                    emailData: "email",
                    smsData: "sms",
                    notificationData: "notification",
                    dashboardData: "dashboard",
                    offloadData: "data offload",
                    eventsEnabledData: "activated event",
                    eventsTriggeredData: "triggered event",
                };

                for (let m in res.data[ownerOrg]) {
                    for (let t in tokenLookup) {
                        const monthIndex = Number(m) - 1;
                        const row = monthlyDataBody[t];
                        const cellValue =
                            res.data[ownerOrg][m][tokenLookup[t]] || 0;

                        if (row.length === 0) {
                            row.push(cellValue);
                        } else {
                            const previous = row[monthIndex] || 0;
                            row[monthIndex] = previous + cellValue;
                        }
                    }
                }

                setMonthlyData((prevState) => ({
                    ...prevState,
                    emailData: monthlyDataBody.emailData,
                    smsData: monthlyDataBody.smsData,
                    notificationData: monthlyDataBody.notificationData,
                    dashboardData: monthlyDataBody.dashboardData,
                    offloadData: monthlyDataBody.offloadData,
                    eventsEnabledData: monthlyDataBody.eventsEnabledData,
                    eventsTriggeredData: monthlyDataBody.eventsTriggeredData,
                }));

                let yearOptions: number[] = [];

                for (let y = yearOfSubscription; y <= moment().year(); y++) {
                    yearOptions.push(y);
                }

                setYearDropdown(yearOptions);
            }
            setIsLoading(false);
        };

        if (isVisible) {
            getDataCount();
        }
    }, [displayedYear, isVisible, orgId]);

    const option = useMemo(() => {

        const chartColor = (attributes?.[0]?.color ?? [])
            .map((colorObj) => {
                const rawColor =
                    typeof colorObj === "string" ? colorObj : colorObj?.[1];
                return parseColorToHex(rawColor ?? "");
            })
            .filter(Boolean);
        return {
            backgroundColor: "#2f3a51",
            responsive: true,
            maintainAspectRatio: false,
            color: chartColor,
            tooltip: {
                trigger: "item",
            },
            legend: {
                data: showLabels,
                bottom: 0,
                left: 10,
                right: 10,
                textStyle: {
                    color: "#ffffff",
                    fontFamily: "Roboto",
                    fontSize: 14,
                    fontWeight: 400,
                },
                padding: 0,
                itemGap: 30,
                icon: "circle",
            },
            xAxis: {
                data: ALL_MONTHS_LIST,
                boundaryGap: true,
                axisLine: {
                    lineStyle: {
                        color: "#48597D",
                    },
                },
            },
            yAxis: {
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "#4B5569",
                    },
                },
            },
            grid: {
                bottom: 58,
                left: 10,
                right: 10,
                top: 29,
                containLabel: true,
            },
            series: getChartSeries(monthlyData),
        };
    }, [monthlyData, getChartSeries, showLabels, attributes]);

    return (
        <WidgetBox ref={visibleRef} className="widget-box w-100">
            <WidgetContent
                title={name || title}
                subTitle={subTitle}
                loading={isLoading}
            >
                <StyledWrapper>
                    <ControllerWrapper>
                        <YearDropdown
                            displayedYear={displayedYear}
                            onChange={setDisplayedYear}
                            yearDropdown={yearDropdown}
                        />
                    </ControllerWrapper>

                    <div style={{ height: "100%", width: "100%" }}>
                        <ReactECharts
                            option={option}
                            theme={"dark"}
                            style={{ height: "100%", width: "100%" }}
                        />
                    </div>
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default memo(EventCounterChart);
